import React, { useCallback, useEffect, useRef, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import CloseIcon from '@mui/icons-material/Close'
import CheckIcon from '@mui/icons-material/Check'
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined'
import { toast } from 'react-toastify'
import fetcher from '../../fetcher'
import { personStatus } from './NewLeadModal'
import './NewLeadModal.scss'
import './HouseholdModal.scss'

const SEARCH_DEBOUNCE_MS = 300
const FILTERS = [['all', 'All'], ['client', 'Clients'], ['lead', 'Current Leads']]

// Why a search result can't be picked, or '' when it can. The backend checks
// again on save; this only keeps the obvious refusals out of the selection.
const unavailable = (person, household) => {
    if (person.household === household.id) return 'Already a member'
    if (person.household) return 'In another household'
    return ''
}

const noun = (n) => (n === 1 ? 'member' : 'members')

// New Household: a name, then the clients and current leads who belong to it.
// Once the household exists, closing the modal any way is "Skip for now".
// Given an `existing` household it opens straight on the member step, and
// closing without adding is a plain cancel.
export const HouseholdModal = ({ open, onClose, onDone, existing = null }) => {
    const [name, setName] = useState('')
    const [error, setError] = useState('')
    const [saving, setSaving] = useState(false)
    const [created, setCreated] = useState(null)
    const household = existing || created
    const householdId = household?.id
    const [query, setQuery] = useState('')
    const [results, setResults] = useState([])
    const [searching, setSearching] = useState(false)
    const [filter, setFilter] = useState('all')
    const [picked, setPicked] = useState([])
    const searchSeq = useRef(0)
    const trimmed = name.trim()

    const finish = (added) => {
        const changed = added || created
        setName('')
        setError('')
        setCreated(null)
        setQuery('')
        setResults([])
        setFilter('all')
        setPicked([])
        if (changed) onDone(household)
        else onClose()
    }
    const close = () => { if (!saving) finish(false) }

    const create = async () => {
        if (!trimmed || saving) return
        setSaving(true)
        setError('')
        try {
            const res = await fetcher.createCrmHousehold({ name: trimmed })
            toast.success(`Created ${res.data.name}.`)
            setCreated(res.data)
        } catch (e) {
            // The axios interceptor toasts the failure; a field error also shows here.
            const field = e?.response?.data?.name
            setError(Array.isArray(field) ? field[0] : field || '')
        } finally {
            setSaving(false)
        }
    }

    // The New Lead search: debounced, 2+ characters, name / email / phone, in
    // the requester's own scope. A late reply to an older term is dropped.
    useEffect(() => {
        const q = query.trim()
        const seq = ++searchSeq.current
        if (!householdId || q.length < 2) {
            setResults([])
            setSearching(false)
            return undefined
        }
        setSearching(true)
        const timer = setTimeout(async () => {
            try {
                const res = await fetcher.getCrmPeople(new URLSearchParams({ search: q }))
                if (seq === searchSeq.current) setResults(res?.data?.results || [])
            } catch (e) {
                if (seq === searchSeq.current) setResults([])
            } finally {
                if (seq === searchSeq.current) setSearching(false)
            }
        }, SEARCH_DEBOUNCE_MS)
        return () => clearTimeout(timer)
    }, [query, householdId])

    // Dead Leads are never offered; someone who is both a client and a lead is
    // one row, so they match either filter.
    const shown = results
        .filter((p) => p.is_client || p.is_lead)
        .filter((p) => filter === 'all' || (filter === 'client' ? p.is_client : p.is_lead))
    const isPicked = (p) => picked.some((x) => x.id === p.id)
    const toggle = (p) => setPicked((prev) => (
        prev.some((x) => x.id === p.id) ? prev.filter((x) => x.id !== p.id) : [...prev, p]
    ))

    // All or nothing server-side. On a refusal the interceptor toasts the
    // server's reason and the selection stays for a retry.
    const addMembers = async () => {
        if (!picked.length || saving) return
        setSaving(true)
        try {
            await fetcher.addCrmHouseholdMembers(household.id, picked.map((p) => p.id))
            toast.success(`Added ${picked.length} ${noun(picked.length)} to ${household.name}.`)
            finish(true)
        } catch (e) {
            // Feedback comes from the interceptor.
        } finally {
            setSaving(false)
        }
    }

    const nameStep = (
        <>
            <div className="nl-head">
                <div className="nl-head-text">
                    <div className="nl-title">New Household</div>
                    <div className="nl-sub">Group a family or business under one name.</div>
                </div>
                <button className="nl-x" onClick={close} aria-label="Close"><CloseIcon style={{ fontSize: 18 }} /></button>
            </div>

            <div className="nl-body">
                <div className="nl-field">
                    <label className="nl-label" htmlFor="household-name">Household name</label>
                    <input
                        id="household-name"
                        value={name}
                        onChange={(e) => { setName(e.target.value); setError('') }}
                        onKeyDown={(e) => { if (e.key === 'Enter') create() }}
                        placeholder="e.g. Johnson Family"
                        maxLength={255}
                        autoFocus
                        autoComplete="off"
                    />
                    {error && <div className="nl-field-error" role="alert">{error}</div>}
                </div>
            </div>

            <div className="nl-foot">
                <button className="nl-btn-ghost" onClick={close} disabled={saving}>Cancel</button>
                <button className="nl-btn-primary" onClick={create} disabled={!trimmed || saving}>
                    {saving ? 'Creating…' : 'Create'}
                </button>
            </div>
        </>
    )

    const q = query.trim()
    let resultList
    if (q.length < 2) resultList = <div className="nl-suggest-empty">Type at least 2 characters to search.</div>
    else if (!shown.length) resultList = <div className="nl-suggest-empty">{searching ? 'Searching…' : 'No clients or current leads match.'}</div>
    else {
        resultList = shown.map((p) => {
            const why = household && unavailable(p, household)
            const on = isPicked(p)
            return (
                <button
                    key={p.id}
                    type="button"
                    className={`nl-suggest-row hh-result${on ? ' on' : ''}`}
                    disabled={Boolean(why)}
                    aria-pressed={on}
                    onClick={() => toggle(p)}
                >
                    <span className="hh-check">{on && <CheckIcon />}</span>
                    <span className="nl-suggest-main">
                        <span className="nl-suggest-name">{p.name || 'Unnamed'}</span>
                        <span className="nl-suggest-meta">
                            {why || [p.phone_number || p.email, p.owner_detail?.name].filter(Boolean).join(' · ') || '—'}
                        </span>
                    </span>
                    <span className={`nl-suggest-tag tag-${personStatus(p).toLowerCase()}`}>{personStatus(p)}</span>
                </button>
            )
        })
    }

    const membersStep = household && (
        <>
            <div className="nl-head">
                <div className="nl-head-ic"><PersonAddOutlinedIcon style={{ fontSize: 20 }} /></div>
                <div className="nl-head-text">
                    <div className="nl-title">Add members to {household.name}</div>
                    <div className="nl-sub">
                        {existing ? '' : 'Household created. '}Choose existing clients and current leads; a person can belong to one household.
                    </div>
                </div>
                <button className="nl-x" onClick={close} aria-label="Close"><CloseIcon style={{ fontSize: 18 }} /></button>
            </div>

            <div className="nl-body">
                <div className="nl-field">
                    <label className="nl-label" htmlFor="household-member-search">Search clients and current leads</label>
                    <input
                        id="household-member-search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Name, email or phone"
                        autoFocus
                        autoComplete="off"
                    />
                </div>
                <div className="hh-filters" role="group" aria-label="Show">
                    {FILTERS.map(([key, label]) => (
                        <button
                            key={key}
                            type="button"
                            className={`hh-filter${filter === key ? ' on' : ''}`}
                            aria-pressed={filter === key}
                            onClick={() => setFilter(key)}
                        >
                            {label}
                        </button>
                    ))}
                </div>
                {picked.length > 0 && (
                    <div className="hh-picked" aria-label="Selected people">
                        {picked.map((p) => (
                            <span key={p.id} className="hh-chip">
                                <span>{p.name || 'Unnamed'}</span>
                                <button type="button" onClick={() => toggle(p)} aria-label={`Remove ${p.name || 'Unnamed'}`}>
                                    <CloseIcon />
                                </button>
                            </span>
                        ))}
                    </div>
                )}
                <div className="hh-results">{resultList}</div>
            </div>

            <div className="nl-foot">
                <button className="nl-btn-ghost" onClick={close} disabled={saving}>{existing ? 'Cancel' : 'Skip for now'}</button>
                <button className="nl-btn-primary" onClick={addMembers} disabled={!picked.length || saving}>
                    {saving ? 'Adding…' : picked.length ? `Add ${picked.length} ${noun(picked.length)}` : 'Add members'}
                </button>
            </div>
        </>
    )

    return (
        <Dialog
            open={open}
            onClose={close}
            maxWidth={false}
            PaperProps={{ className: `nl-modal${household ? ' hh-members-modal' : ''}` }}
        >
            {household ? membersStep : nameStep}
        </Dialog>
    )
}

// Add to household, from Person Detail: one person into one household, picked
// from those the requester manages or named new. Any other household would
// 403, which the axios interceptor turns into a /403 redirect.
export const AddToHouseholdModal = ({ open, person, onClose, onDone }) => {
    const [query, setQuery] = useState('')
    const [rows, setRows] = useState([])
    const [page, setPage] = useState(1)
    const [hasMore, setHasMore] = useState(false)
    const [loading, setLoading] = useState(true)
    const [picked, setPicked] = useState(null)
    const [naming, setNaming] = useState(false)
    const [name, setName] = useState('')
    const [error, setError] = useState('')
    const [saving, setSaving] = useState(false)
    // Created, but adding the person failed: a retry reuses it.
    const [created, setCreated] = useState(null)
    const searchSeq = useRef(0)
    const trimmed = name.trim()
    const personName = person?.name || 'this person'

    const fetchPage = useCallback(async (seq, q, nextPage) => {
        setLoading(true)
        try {
            const params = new URLSearchParams({ page: nextPage })
            if (q) params.set('search', q)
            const res = await fetcher.getCrmHouseholds(params)
            if (seq !== searchSeq.current) return
            const results = res?.data?.results || []
            setRows((prev) => (nextPage === 1 ? results : [...prev, ...results]))
            setPage(nextPage)
            setHasMore(Boolean(res?.data?.has_more))
        } catch (e) {
            if (seq === searchSeq.current && nextPage === 1) { setRows([]); setHasMore(false) }
        } finally {
            if (seq === searchSeq.current) setLoading(false)
        }
    }, [])

    // Every household on open, then by name; a late reply to an older term is dropped.
    useEffect(() => {
        const seq = ++searchSeq.current
        if (!open) return undefined
        const timer = setTimeout(() => fetchPage(seq, query.trim(), 1), SEARCH_DEBOUNCE_MS)
        return () => clearTimeout(timer)
    }, [open, query, fetchPage])

    const loadMore = () => { if (!loading) fetchPage(searchSeq.current, query.trim(), page + 1) }

    const shown = rows.filter((h) => h.can_manage)
    const target = naming ? created : shown.find((h) => h.id === picked)

    const finish = (household) => {
        setQuery('')
        setPicked(null)
        setNaming(false)
        setName('')
        setError('')
        setCreated(null)
        if (household) onDone(household)
        else onClose()
    }
    const close = () => { if (!saving) finish(null) }

    const startNew = () => {
        setNaming(true)
        setPicked(null)
        if (!name) setName(query.trim())
    }

    // On a refusal the interceptor toasts the server's reason and the modal stays.
    const submit = async () => {
        if (saving || (naming ? !trimmed : !target)) return
        setSaving(true)
        setError('')
        let household = target
        try {
            if (!household) {
                try {
                    household = (await fetcher.createCrmHousehold({ name: trimmed })).data
                } catch (e) {
                    const field = e?.response?.data?.name
                    setError(Array.isArray(field) ? field[0] : field || '')
                    return
                }
            }
            await fetcher.addCrmHouseholdMembers(household.id, [person.id])
            toast.success(`Added ${personName} to ${household.name}.`)
            finish(household)
        } catch (e) {
            // Feedback comes from the interceptor. Keep a new household for the retry.
            if (naming) setCreated(household)
        } finally {
            setSaving(false)
        }
    }

    let resultList
    if (!shown.length) {
        resultList = (
            <div className="nl-suggest-empty">
                {loading ? 'Loading…' : hasMore ? 'No households on this page.' : 'No households you manage match.'}
            </div>
        )
    } else {
        resultList = shown.map((h) => {
            const on = h.id === picked
            return (
                <button
                    key={h.id}
                    type="button"
                    className={`nl-suggest-row hh-result${on ? ' on' : ''}`}
                    aria-pressed={on}
                    onClick={() => setPicked(h.id)}
                >
                    <span className="hh-check">{on && <CheckIcon />}</span>
                    <span className="nl-suggest-main">
                        <span className="nl-suggest-name">{h.name}</span>
                        <span className="nl-suggest-meta">{h.member_count} {noun(h.member_count)}</span>
                    </span>
                </button>
            )
        })
    }

    const listBody = (
        <>
            <div className="nl-field">
                <label className="nl-label" htmlFor="household-search">Search households</label>
                <input
                    id="household-search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Household name"
                    autoFocus
                    autoComplete="off"
                />
            </div>
            <div className="hh-results">
                <button type="button" className="nl-suggest-row hh-result hh-new" onClick={startNew}>
                    <span className="nl-suggest-main"><span className="nl-suggest-name">+ New household</span></span>
                </button>
                {resultList}
                {hasMore && (
                    <button type="button" className="nl-suggest-row hh-result hh-more" onClick={loadMore} disabled={loading}>
                        <span className="nl-suggest-main">
                            <span className="nl-suggest-name">{loading ? 'Loading…' : 'Load more'}</span>
                        </span>
                    </button>
                )}
            </div>
        </>
    )

    const nameBody = (
        <div className="nl-field">
            <label className="nl-label" htmlFor="new-household-name">Household name</label>
            <input
                id="new-household-name"
                value={name}
                onChange={(e) => { setName(e.target.value); setError('') }}
                onKeyDown={(e) => { if (e.key === 'Enter') submit() }}
                placeholder="e.g. Johnson Family"
                maxLength={255}
                readOnly={Boolean(created)}
                autoFocus
                autoComplete="off"
            />
            {error && <div className="nl-field-error" role="alert">{error}</div>}
            {created && (
                <div className="nl-field-error" role="status">
                    {created.name} was created, but {personName} wasn't added. Try again.
                </div>
            )}
        </div>
    )

    let submitLabel = 'Add to household'
    if (saving) submitLabel = 'Adding…'
    else if (created) submitLabel = 'Try again'
    else if (naming) submitLabel = 'Create and add'

    return (
        <Dialog open={open} onClose={close} maxWidth={false} PaperProps={{ className: 'nl-modal hh-members-modal' }}>
            <div className="nl-head">
                <div className="nl-head-text">
                    <div className="nl-title">Add {personName} to a household</div>
                    <div className="nl-sub">A person can belong to one household.</div>
                </div>
                <button className="nl-x" onClick={close} aria-label="Close"><CloseIcon style={{ fontSize: 18 }} /></button>
            </div>

            <div className="nl-body">{naming ? nameBody : listBody}</div>

            <div className="nl-foot">
                {naming && !created ? (
                    <button className="nl-btn-ghost" onClick={() => setNaming(false)} disabled={saving}>Back</button>
                ) : (
                    <button className="nl-btn-ghost" onClick={close} disabled={saving}>Cancel</button>
                )}
                <button className="nl-btn-primary" onClick={submit} disabled={saving || (naming ? !trimmed : !target)}>
                    {submitLabel}
                </button>
            </div>
        </Dialog>
    )
}
